import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import { encode } from 'qss';

import type TTConfig from './TTConfig';
import type TTFiltering from './TTFiltering';
import type TTQuery from './TTQuery';
import type { RecommendationPreviewRequest } from './RecommendationPreviewRequest';
import type {
    GetContentRefsParams,
    ITamTamyAPIClient,
    PaginatedResult
} from './ITamTamyAPIClient';
import type { ICMSRuntimeClient } from './ICMSRuntimeClient';
import type { PaginationInfo } from './PaginationInfo';

const defaultConfig: TTConfig = {
    endpoint: 'http://tamtamy.replynet.prv/tamtamy/api'
};

export interface Config {
    axiosInstance?: AxiosInstance;
}

const FIELDS_WITHOUT_DOUBLE_QUOTES: string[] = ['orBehaviorFields', 'status'];

function prepareFilteringQueryEngine(filtering: TTFiltering): string {
    let q = '';
    if (filtering?.value) {
        let value: string;
        let orMode: boolean;
        if (Array.isArray(filtering.value)) {
            value = `|${[...new Set(filtering.value)].join(' OR ')}|`;
            orMode = true;
        } else {
            value = filtering.value;
            orMode = value.startsWith('|') && value.endsWith('|');
        }

        const field = filtering.columnField;
        const fieldValue = orMode || FIELDS_WITHOUT_DOUBLE_QUOTES.includes(field)
            ? `${value}`
            : `"${value}"`;

        const notBehaviorFields = new Set();
        const strBehaviorEq = new Set();
        const strBehaviorBegin = new Set();
        const strBehaviorEnd = new Set();
        switch (filtering.operatorValue) {
            case 'not':
                notBehaviorFields.add(field);
                q += `-${field}(${fieldValue})`;
                break;
            case 'equals':
            case 'is':
                strBehaviorEq.add(field);
                q += `-${field}(${fieldValue})`;
                break;
            case 'startsWith':
                strBehaviorBegin.add(field);
                q += `-${field}(${fieldValue})`;
                break;
            case 'endsWith':
                strBehaviorEnd.add(field);
                q += `-${field}(${fieldValue})`;
                break;
            case 'after':
            case 'onOrAfter':
                q += `-dateField(${field})-fromDate(${value})`;
                break;
            case 'before':
            case 'onOrBefore':
                q += `-dateField(${field})-toDate(${value})`;
                break;
            // case 'contains':
            default:
                q += `-${field}(${fieldValue})`;
                break;
        }
        for (const f of notBehaviorFields) {
            q += `-notBehaviorFields(${f})`;
        }
        for (const f of strBehaviorEq) {
            q += `-strBehaviorEq(${f})`;
        }
        for (const f of strBehaviorBegin) {
            q += `-strBehaviorBegin(${f})`;
        }
        for (const f of strBehaviorEnd) {
            q += `-strBehaviorEnd(${f})`;
        }
    }
    return q;
}

export class TamTamyAPIClient implements ITamTamyAPIClient, ICMSRuntimeClient {

    private constructor(
        public readonly axiosInstance: AxiosInstance
    ) {}

    public static build(config: TTConfig = defaultConfig): TamTamyAPIClient {
        const instance = axios.create({
            baseURL: config.endpoint,
            paramsSerializer: (params: object) => encode(params),
            ...(config.axiosConfig || {})
        });
        config.axiosConfigFn?.(instance);
        config.authProvider?.configure(instance);
        return new TamTamyAPIClient(instance);
    }


    /* Content API */
    async listContents<T = any>(queryParams: TTQuery = {
        firstItem: 0,
        pageSize: 10
    }, { axiosInstance = this.axiosInstance }: Config = {}): Promise<{ data: { list: T[], paginationInfo: PaginationInfo } }> {
        const {
            firstItem = 0,
            pageSize = 10,
            sorting,
            filtering,
            include,
            distinct,
            netAdminStatusOverride,
            netAdminPermissionOverride,
            alternativeResource,
            shareMode,
        } = queryParams;
        const params = {
            'list.firstResult': firstItem,
            'list.numberOfResult': pageSize,
            'list.netAdminStatusOverride': netAdminStatusOverride || [],
            'list.netAdminPermissionOverride': netAdminPermissionOverride || [],
        };
        if (shareMode) {
            params['list.shareMode'] = shareMode;
        }
        if (sorting) {
            const capitalizedSort = sorting.sort.charAt(0).toUpperCase() + sorting.sort.slice(1);
            params['list.query'] = `-orderBy${capitalizedSort}(${sorting.field})`;
        }
        const filters: TTFiltering[] = Array.isArray(filtering) ? filtering : [filtering];
        for (const filter of filters) {
            if (filter?.value) {
                const filters = {...filter};
                if (filter.columnField === 'type') {
                    filters.columnField = 'contentTypeId';
                }
                params['list.query'] = (params['list.query'] || '') + prepareFilteringQueryEngine(filters);
            }
        }
        if (distinct) {
            params['list.query'] = `${params['list.query'] || ''}-distinct()`;
        }
        if (include) {
            params['include'] = include;
        }
        const url = alternativeResource || '/content.json';
        try {
            return await axiosInstance.get<{ list: T[], paginationInfo: PaginationInfo }>(url, { params });
        } catch (e) {
            console.error(`[query] GET ${url}, ${JSON.stringify(params)}`, e);
            throw e;
        }
    }

    async deleteContent(contentId: number): Promise<any> {
        return this.axiosInstance.delete(
            `/content/${contentId}.json`
        );
    }

    async getContent<T = any>(contentId: number, params: object = {}, config: object = {}): Promise<AxiosResponse<T>> {
        return this.axiosInstance.get<T>(
            `/content/${contentId}.json`,
            {
                params,
                ...config
            }
        );
    }

    async createContent(content: any): Promise<any> {
        return this.axiosInstance.post(
            '/content.json',
            {
                content
            }
        );
    }

    async createContent2(content: any, onlyIfItDoesNotExists: boolean = false): Promise<{id: number}> {
        const { data } = await this.axiosInstance.post('/create-content', {
            content,
            onlyIfItDoesNotExists,
        }, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return data;
    }

    async updateContent(contentId: number, content: any): Promise<any> {
        return this.axiosInstance.post(
            '/content.json',
            {
                id: contentId,
                content
            }
        );
    }

    async updateContentString(contentId: number, content: string): Promise<any> {
        return this.axiosInstance.post(
            '/content.json',
            {
                id: contentId,
                contentJson: content
            }
        );
    }

    /* File Upload API */
    async uploadFile(file: File): Promise<any> {
        const formData = new FormData();
        formData.append('fileRequestBean.file', file);
        return this.axiosInstance.post('/file.json', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    /* Content Types API */
    async listContentTypes(firstItem: number = 0, pageSize: number = 10): Promise<any> {
        const params = {
            'list.firstResult': firstItem,
            'list.numberOfResult': pageSize,
        };

        const url = '/content-type.json';
        try {
            return await this.axiosInstance.get(url, { params });
        } catch (e) {
            console.error(`[query] GET ${url}, ${JSON.stringify(params)}`, e);
            throw e;
        }
    }

    async getContentType(contentType: string): Promise<any> {
        return this.axiosInstance.get(`/content-type/${contentType}.json`);
    }

    /* Group Types API */
    async listGroupTypes(firstItem = 0, pageSize = 10): Promise<any> {
        const params = {
            'list.firstResult': firstItem,
            'list.numberOfResult': pageSize,
        };
        return this.axiosInstance.get('/group-type.json', { params });
    }

    async getGroupType(groupTypeId: string): Promise<any> {
        return this.axiosInstance.get(`/group-type/${groupTypeId}.json`);
    }


    /* Groups API */
    async listGroups(firstItem = 0, pageSize = 10, groupTypeId?: string): Promise<any> {
        let query = '-orderByAsc(groupName)';
        if (groupTypeId) {
            query += `-groupTypeId(${groupTypeId})`;
        }
        return this.axiosInstance.get('/group.json', {
            params: {
                'list.firstResult': firstItem,
                'list.numberOfResult': pageSize,
                'list.personalAdminOverride': true,
                'list.query': query,
            }
        });
    }

    async getGroup(groupId: string): Promise<any> {
        return this.axiosInstance.get(`/group/${groupId}.json`);
    }

    /* Languages API */
    async listLanguages(): Promise<any> {
        return this.axiosInstance.get('/language.json');
    }

    /* Content Search API*/
    async searchContents(requestBean: any): Promise<any> {
        return this.axiosInstance.post('/content-search.json', requestBean);
    }

    /* Content Copy API */
    async contentCopy(contentId: number, override?: any): Promise<any> {
        return this.axiosInstance.post('/content-copy.json', {
            id: contentId,
            override
        });
    }

    async createSite(siteKey: string, siteName: string): Promise<void> {
        await this.axiosInstance.post('/site.json', {
            site: {
                id: siteKey,
                name: siteName
            }
        });
    }

    /* Tags API */
    async listTags(keyword: string): Promise<{ data: { list: { tag: string }[] } }> {
        return this.axiosInstance.get('/tag.json', {params: {q: keyword}});
    }

    /* Recommendation API */
    async previewRecommendations(request: RecommendationPreviewRequest, config: { include?: string[] } = {}): Promise<any[]> {
        const url = '/content-recommendation-preview.json';
        const params = {
            data: request,
            include: config.include
        };
        try {
            const { data } = await this.axiosInstance.post(url, params);
            return data.list;
        } catch (e) {
            console.error(`[query] GET ${url}, ${JSON.stringify(params)}`, e);
            throw e;
        }
    }

    async getRelatedMetaOptions(contentType: string, metaKey: string, code: string, depMetaKey: string): Promise<any> {
        const url = `/content-meta-values-relations/${contentType}.json`;
        const params = {
            metaKey,
            code,
            depMetaKey,
        }
        try {
            const { data } = await this.axiosInstance.get(url, { params });
            return data;
        } catch (e) {
            console.error(`[query] GET ${url}, ${JSON.stringify(params)}`, e);
            throw e;
        }
    }

    async getContentRefs<T>(params: GetContentRefsParams): Promise<[T[], string]> {
        const { data } = await this.axiosInstance.get<PaginatedResult<T>>('/content-ref.json', {
            params,
        });
        return [data.items, data.paginationToken];
    }

    async getContentRef<T>(id: number): Promise<T> {
        const { data } = await this.axiosInstance.get<T>(`/content-ref/${id}.json`);
        return data;
    }

    async getContentPageRefs<T>(params: {
        pageKey?: string;
        contentCanonicalId?: number;
    }): Promise<T[]> {
        const { data = [] } = await this.axiosInstance.get<T[]>('/canonicals-paperbits-page-refs.json', {
            params
        });
        return data;
    }

    async getContentByPermalink<T>(permalink: string): Promise<T> {
        const { data } = await this.axiosInstance.get<T>(`/content-permalink/${permalink}.json`);
        return data;
    }

    async getCmsContentTranslationById(id: string, targetLanguage: string): Promise<{ id: number } | null> {
        const { data } = await this.axiosInstance.get<{ id: number }>(`/content-cluster/${id}.json?lang=${targetLanguage}`)
        return data;
    }

    async approveContent(id: number): Promise<unknown> {
        const { data } = await this.axiosInstance.post('/content-approval.json', {
            contentId: id,
        })
        return data;
    }
}
